import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { RouteParam, ServiceBase } from '../service-base';
import { Announcement } from 'src/app/@shared/models/announcements/announcement.model';
import { Logger } from '../logger/logger.service';

const LOGGER = new Logger('AnnouncementService');

const CLASS_NAME = 'Announcement';
const ENDPOINT = `/announcements/`;
const ROUTES: RouteParam[] = [
    { key: 'read', path: '{id}/read/' },
    { key: 'readAll', path: 'read-all/' },
];

/**
 * Annonces publiées par Biifor.
 *
 * Un seul appel sert les deux usages : l'historique affiche tout, la mise en avant filtre
 * sur `highlighted`. Les annonces chargées sont donc conservées ici et partagées par la
 * pastille, le panneau et l'écran d'historique.
 *
 * Les erreurs sont journalisées sans être propagées : une fonctionnalité purement
 * informative ne doit pas interrompre l'utilisateur.
 *
 * Contrat d'API : `b4cash-srv/documentation/announcements/ANNOUNCEMENTS_API.md`
 */
@Injectable({
    providedIn: 'root'
})
export class AnnouncementService extends ServiceBase {

    private _announcements$: BehaviorSubject<Announcement[]> = new BehaviorSubject<Announcement[]>([]);
    public readonly announcements$: Observable<Announcement[]> = this._announcements$.asObservable();

    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addRoutes(ROUTES);
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /** Toutes les annonces visibles, antéchronologiques. */
    public get announcements(): Announcement[] {
        return this._announcements$.getValue();
    }

    /** Les annonces à mettre en avant : pastille, panneau et popups. */
    public get highlighted(): Announcement[] {
        return this.announcements.filter(announcement => announcement.highlighted);
    }

    /**
     * Les annonces à ouvrir en popup, dans l'ordre de publication (la plus ancienne
     * d'abord) : les popups s'empilent chronologiquement.
     */
    public get toPopup(): Announcement[] {
        return this.highlighted
            .filter(announcement => announcement.needsPopup)
            .slice()
            .reverse();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Charge toutes les annonces visibles et les publie sur `announcements$`.
     */
    public loadAll$(): Observable<Announcement[]> {
        return this.getList$().pipe(
            map((announcements: Announcement[]) => announcements || []),
            tap((announcements: Announcement[]) => this._announcements$.next(announcements)),
            catchError((error: any) => {
                LOGGER.error('Erreur lors du chargement des annonces', error);
                this._announcements$.next([]);
                return of([]);
            })
        );
    }

    /**
     * Marque une annonce comme lue pour l'utilisateur courant. L'appel est idempotent.
     */
    public markAsRead$(announcement: Announcement): Observable<boolean> {
        if (!announcement?.id || announcement.read) {
            return of(true);
        }
        const url = this.routes.read.replace('{id}', announcement.id.toString());
        return this.httpClient.post(url, {}).pipe(
            map(() => {
                this._applyRead([announcement]);
                return true;
            }),
            catchError((error: any) => {
                LOGGER.error('Erreur lors du marquage de l\'annonce comme lue', error);
                return of(false);
            })
        );
    }

    /**
     * Marque comme lues toutes les annonces actuellement mises en avant. Les annonces
     * expirées ou antérieures à l'inscription ne sont pas touchées : elles n'étaient déjà
     * pas mises en avant.
     */
    public markAllAsRead$(): Observable<boolean> {
        const highlighted = this.highlighted;
        if (highlighted.length === 0) {
            return of(true);
        }
        return this.httpClient.post(this.routes.readAll, {}).pipe(
            map(() => {
                this._applyRead(highlighted);
                return true;
            }),
            catchError((error: any) => {
                LOGGER.error('Erreur lors du marquage de toutes les annonces comme lues', error);
                return of(false);
            })
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Reporte la lecture localement plutôt que de recharger la liste : le serveur ne
     * renvoie pas l'annonce mise à jour, et un rechargement ferait clignoter le panneau.
     */
    private _applyRead(announcements: Announcement[]): void {
        announcements.forEach(announcement => {
            announcement.read = true;
            announcement.highlighted = false;
        });
        this._announcements$.next([...this.announcements]);
    }
}
